// Lib
import { NativeTypes } from 'react-dnd-html5-backend';

// Components
import ElementDropTarget from '../element/dnd/elementDropTargets/ElementDropTarget';
import getRangeIndex from './getRangeIndex';

// Utils
import { length } from '../../common/utils/immutableHelper';
import { getWrappedReactDndMonitor } from '../utils/dnd/GlobalDragAndDropHelper';
import { getListChildNewLocation } from '../../common/elements/utils/elementLocationUtils';
import {
    dropTargetAlreadyHandled,
    handleElementDrop,
    handleFileDrop,
    handleImageUrlDrop,
    handleTaskDrop,
    handleTaskListDropOnTasks,
    handleTextDrop,
    handleUrlDrop,
    NATIVE_TYPES,
    CUSTOM_TYPES,
} from '../utils/dnd/dragAndDropUtils';
import { isColumn, isCommentThread, isLine, isTask, isTaskList } from '../../common/elements/utils/elementTypeUtils';
import isDraggedItemATask from '../element/dnd/task/isDraggedItemATask';
import areDraggedItemsTaskListsWithoutHeadings from '../element/dnd/task/areDraggedItemsTaskListsWithoutHeadings';
import {
    getElementId,
    getIsCollapsed,
    getLocationParentId,
    getLocationSection,
} from '../../common/elements/utils/elementPropertyUtils';
import { getRequiredSavePermission } from '../../common/elements/utils/elementTypePermissionsUtils';
import { hasPermission } from '../../common/permissions/permissionUtil';

// Constants
import { BoardSections } from '../../common/boards/boardConstants';
import { DROP_TARGET_TYPES } from '../../common/elements/elementConstants';
import { FILE_TYPE } from '../../common/files/fileConstants';

const getLocationOnList = (props, monitor) => {
    // Use the item's index, or add it to the end of the list
    const { listId, childElementIds } = props;
    const item = monitor.getItem();

    const index = item.index || item.index === 0 ? item.index : length(childElementIds);

    // Construct location object for the drop
    return getListChildNewLocation({ listId, index });
};

const getAcceptedElementIds = (props, monitor) => {
    const dropTargetElement = props.element;

    const { draggedElements } = monitor.getItem();
    return draggedElements
        .filter(
            (draggedEl) =>
                !isLine(draggedEl) &&
                !(isColumn(dropTargetElement) && isColumn(draggedEl)) &&
                !(getIsCollapsed(draggedEl) && isCommentThread(draggedEl)),
        )
        .map(getElementId);
};

// FIXME This function gets called way more than it needs to be. It would be great to throttle this somehow.
//  NOTE: We cannot simply wrap in a rafThrottle (we tried this and broke sub-list dnd)
//      This is because the function is shared between every instance of the decorator.
//      It needs to be a throttled function *per* instance of the component.
//      I couldn't find a performant simple way of doing this.
const hoverFn = (props, monitor, component) => {
    if (!component || !monitor || !monitor.canDrop()) return;

    const { showPlaceholderForDraggedElement = false, getRangeArray, getContextZoomScale } = props;

    const zoomScale = getContextZoomScale();

    // Determine rectangle on screen
    const item = monitor.getItem();
    const { element, draggedElementIds, dragStartTimestamp } = item;

    const hoverBoundingRectTop = component.getBoundingRectTop(dragStartTimestamp);

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();
    if (!clientOffset) return;

    // Get pixels to the top
    const hoverPositionFromListTop =
        clientOffset.y - hoverBoundingRectTop - component.getOffsetTop(dragStartTimestamp) * zoomScale;

    const rangeArray = getRangeArray(dragStartTimestamp);

    // Find the index that the hovered item is currently hovering over
    let hoveredIndex = getRangeIndex(hoverPositionFromListTop, rangeArray, zoomScale, draggedElementIds);

    let showPlaceholder = true;

    if (draggedElementIds && draggedElementIds.length === 1) {
        const hoveringItemId = getElementId(element);
        const hoveringItemParentId = getLocationParentId(element);
        const hoveringItemSection = getLocationSection(element);

        const { listId, childElementIds } = props;

        // If dragging an item from the current list, then check the index because
        // the placeholder should only be shown if its index will change.
        if (listId === hoveringItemParentId && hoveringItemSection === BoardSections.INBOX) {
            const currentIndex = childElementIds.reduce(
                (acc, childId, index) => (hoveringItemId === childId ? index : acc),
                null,
            );

            // If the index is the current position, or the next position (which would result in the same position
            // anyway) then don't show the placeholder, and just set the index to the current position so that the
            // move event won't fire. (There's no point firing a move event when nothing changes).
            if (currentIndex === hoveredIndex || currentIndex + 1 === hoveredIndex) {
                hoveredIndex = currentIndex;
                showPlaceholder = showPlaceholderForDraggedElement || false;
            }
        }
    }

    // Set on the item so that it can drop it at the correct index in the drop handler
    monitor.getItem().index = hoveredIndex;
    if (
        (!showPlaceholder && component.state.hoveredIndex === null) ||
        (showPlaceholder && hoveredIndex === component.state.hoveredIndex)
    ) {
        return;
    }

    // If the placeholder should be shown, set the hovered index, otherwise set it to null
    component.setState({
        hoveredIndex: showPlaceholder ? hoveredIndex : null,
    });
};

const handleListTaskDrop = handleTaskDrop(getLocationOnList);
const handleListTaskListDrop = handleTaskListDropOnTasks(getLocationOnList);
const handleListElementDrop = handleElementDrop(getLocationOnList);
const handleListFileDrop = handleFileDrop(getLocationOnList);
const handleListTextDrop = handleTextDrop(getLocationOnList);
const handleListUrlDrop = handleUrlDrop(getLocationOnList);
const handleListImageUrlDrop = handleImageUrlDrop(getLocationOnList);

// /* Drop Target functionality. */
export const listDropLocationFn = (props, dndMonitor, component) => {
    const monitor = getWrappedReactDndMonitor(dndMonitor);

    const { element } = props;

    // Don't handle the drop if it's already been handled by a child.
    if (dropTargetAlreadyHandled(monitor, component)) return;

    if (monitor.getItemType() === CUSTOM_TYPES.IMAGE_LINK) return handleListImageUrlDrop(props, monitor, component);
    if (monitor.getItemType() === NativeTypes.FILE) return handleListFileDrop(props, monitor, component);
    if (monitor.getItemType() === NativeTypes.TEXT) return handleListTextDrop(props, monitor, component);
    if (monitor.getItemType() === NativeTypes.URL) return handleListUrlDrop(props, monitor, component);
    if (!(isTask(element) || isTaskList(element)) && isDraggedItemATask(props, monitor)) {
        return handleListTaskDrop(props, monitor, component);
    }

    if ((isTask(element) || isTaskList(element)) && areDraggedItemsTaskListsWithoutHeadings(props, monitor)) {
        return handleListTaskListDrop(props, monitor, component);
    }

    const acceptedElementIds = getAcceptedElementIds(props, monitor);
    return handleListElementDrop(props, monitor, component, acceptedElementIds);
};

const dropTargetConfig = {
    drop: listDropLocationFn,
    canDrop: (props, monitor) => {
        const { listCanDropFn, permissions } = props;
        const item = monitor.getItem();
        const itemType = monitor.getItemType();

        const elementType = itemType === NativeTypes.FILE ? FILE_TYPE : item?.element;

        const requiredPermissions = getRequiredSavePermission(elementType);
        return listCanDropFn(props, monitor) && hasPermission(requiredPermissions, permissions);
    },
    acceptDropTypes: NATIVE_TYPES,
    hover: hoverFn,
};

const listDropCollect = (connector, monitor) => ({
    connectDropTarget: connector.dropTarget(),
    isHovered: monitor.canDrop() && monitor.isOver({ shallow: true }),
    isHoveredDeep: monitor.canDrop() && monitor.isOver({ shallow: false }),
    isOverShallow: monitor.isOver({ shallow: true }),
});

export default ElementDropTarget(dropTargetConfig, DROP_TARGET_TYPES.LIST, listDropCollect);

export const customListDropTargetDecorator = (
    config = dropTargetConfig,
    types = DROP_TARGET_TYPES.LIST,
    collect = listDropCollect,
) => ElementDropTarget(config, types, collect);
